import { useState } from "react";
import { useParams, useLocation } from "wouter";
import {
  useGetEmailLog,
  getGetEmailLogQueryKey,
  useGetBrandSettings,
  useAiDraftFollowUp,
} from "@workspace/api-client-react";
import { toast } from "sonner";
import { VerifiedSenderBadge } from "@/components/VerifiedSenderBadge";
import { FailedVerificationWarning } from "@/components/FailedVerificationWarning";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  ArrowLeft,
  AlertCircle,
  Clock,
  Code,
  Mail,
  ChevronDown,
  ChevronRight,
  Copy,
  Reply,
  Send,
  ArrowDownRight,
  ExternalLink,
  Paperclip,
  Eye,
  MousePointerClick,
} from "lucide-react";
import { Link } from "wouter";
import {
  COMPOSE_PREFILL_KEY,
  type ComposePrefill,
} from "@/lib/composePrefill";
import { apiErrorMessage, apiErrorStatus } from "@/lib/apiError";
import { useAiRateLimitUpdater } from "@/lib/aiRateLimit";
import { rewriteLinksForPreview } from "@/lib/previewHtml";
import { AiRateLimitInline } from "@/components/AiRateLimitInline";

const FOLLOWUP_STORAGE_KEY = "mailcraft:compose-followup";

const DEFAULT_FROM_NAME = "MailCraft";

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export default function LogDetail() {
  const { id } = useParams();
  const [, setLocation] = useLocation();
  const logId = parseInt(id || "0", 10);
  const [showProviderDetails, setShowProviderDetails] = useState(false);

  const { data: log, isLoading } = useGetEmailLog(logId, {
    query: { enabled: !!logId, queryKey: getGetEmailLogQueryKey(logId) }
  });
  const { data: brand } = useGetBrandSettings();
  const followUpMutation = useAiDraftFollowUp();
  const aiRateLimit = useAiRateLimitUpdater();

  const handleDraftFollowUp = async () => {
    try {
      const res = await followUpMutation.mutateAsync({
        data: { emailLogId: logId },
      });
      aiRateLimit.updateFromResponse(res);
      window.sessionStorage.setItem(
        FOLLOWUP_STORAGE_KEY,
        JSON.stringify({
          to: res.to,
          subject: res.subject,
          bodyHtml: res.bodyHtml,
        }),
      );
      toast.success("Follow-up drafted — opening Compose");
      setLocation("/app/compose?followUp=1");
    } catch (e) {
      const status = apiErrorStatus(e);
      if (status === 429) {
        aiRateLimit.updateFromError(e);
      }
      toast.error(
        status === 429
          ? `AI rate limit reached: ${apiErrorMessage(e, "try again later")}`
          : apiErrorMessage(e, "Follow-up draft failed"),
      );
    }
  };

  if (isLoading || !log) {
    return <div className="flex-1 flex justify-center items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const rawHtml = log.html ?? "";
  const rawText = log.text ?? "";
  const hasDuplicableContent = !!rawHtml.trim() || !!rawText.trim();

  const handleDuplicateToCompose = () => {
    if (!hasDuplicableContent) {
      toast.error("This log has no content to copy.");
      return;
    }
    // If the original was a text-only send, wrap it in a minimal HTML
    // container so Compose's blank-mode send (which sends `html`) still has
    // something to deliver instead of an empty body.
    const html =
      rawHtml.trim()
        ? rawHtml
        : `<pre style="font-family: -apple-system, Segoe UI, Roboto, sans-serif; white-space: pre-wrap; margin: 0;">${escapeHtml(
            rawText,
          )}</pre>`;
    const prefill: ComposePrefill = {
      to: log.to ?? "",
      cc: log.cc ?? "",
      bcc: log.bcc ?? "",
      subject: log.subject ?? "",
      html,
      text: rawText,
      sourceLogId: log.id,
    };
    try {
      sessionStorage.setItem(COMPOSE_PREFILL_KEY, JSON.stringify(prefill));
    } catch {
      toast.error("Couldn't stage the email for Compose. Try again.");
      return;
    }
    setLocation("/app/compose");
  };

  return (
    <div className="flex-1 flex flex-col h-[calc(100vh-64px)] md:h-screen overflow-hidden bg-background">
      <div className="h-16 px-6 border-b border-border flex items-center justify-between shrink-0 bg-card gap-4">
        <div className="flex items-center min-w-0">
          <Button variant="ghost" size="icon" className="mr-4 shrink-0" asChild>
            <Link href="/app/logs"><ArrowLeft className="w-5 h-5" /></Link>
          </Button>
          <h1 className="font-bold text-lg truncate">Log Details</h1>
        </div>
        <Button
          variant="default"
          onClick={handleDuplicateToCompose}
          data-testid="button-duplicate-to-compose"
          title="Open this email in Compose as a fresh, editable draft"
          className="ml-auto"
        >
          <Copy className="w-4 h-4 mr-2" />
          Use as new email
        </Button>
        {log.status === "sent" && (
          <div className="flex items-center gap-2 shrink-0">
            <AiRateLimitInline />
            <Button
              type="button"
              variant="outline"
              className="shrink-0"
              onClick={handleDraftFollowUp}
              disabled={followUpMutation.isPending}
              data-testid="button-draft-followup-detail"
            >
              {followUpMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Reply className="w-4 h-4 mr-2" />
              )}
              Draft follow-up
            </Button>
          </div>
        )}
      </div>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Meta Side */}
        <div className="w-full lg:w-[400px] flex flex-col border-r border-border bg-card overflow-auto shrink-0">
          <div className="p-6 space-y-6">
            <div>
              <div className="mb-4">
                {log.status === "sent" ? (
                  <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20 px-3 py-1 text-sm">Sent Successfully</Badge>
                ) : log.status === "queued" ? (
                  <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20 px-3 py-1 text-sm">Queued — sending now</Badge>
                ) : (
                  <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20 px-3 py-1 text-sm">Failed to Send</Badge>
                )}
              </div>
              <h2 className="text-xl font-semibold break-words leading-tight">{log.subject}</h2>
            </div>

            {(() => {
              const historicalName = log.fromName;
              const historicalNameForCompare =
                historicalName ?? DEFAULT_FROM_NAME;
              const currentName =
                (brand?.fromName?.trim() ? brand.fromName.trim() : null) ??
                DEFAULT_FROM_NAME;
              const nameChanged =
                !!brand &&
                historicalName !== null &&
                historicalNameForCompare !== currentName;
              return (
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="log-detail-from-name">From name</Label>
                    <Input
                      id="log-detail-from-name"
                      value={historicalName ?? "(none)"}
                      readOnly
                      className={`h-9 bg-muted/40 text-xs ${
                        historicalName === null ? "text-muted-foreground italic" : ""
                      }`}
                      title={
                        historicalName === null
                          ? "No display name was recorded for this send."
                          : "The display name recipients saw at send time."
                      }
                      data-testid="log-detail-from-name-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="log-detail-from-address">From address</Label>
                    <Input
                      id="log-detail-from-address"
                      value={log.fromEmail}
                      readOnly
                      className="h-9 bg-muted/40 font-mono text-xs"
                      title="The sender email address recipients saw at send time."
                      data-testid="log-detail-from-input"
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    This is what recipients saw — captured at send time.
                  </p>
                  {nameChanged && (
                    <p
                      className="text-[11px] text-muted-foreground"
                      data-testid="log-detail-from-name-changed-note"
                    >
                      Your current Brand Settings From name is{" "}
                      <span className="font-medium text-foreground">
                        {currentName}
                      </span>
                      . Future emails will use that name instead.
                    </p>
                  )}
                </div>
              );
            })()}

            <Card className="shadow-none border-border">
              <CardContent className="p-4 space-y-4 text-sm">
                <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
                  <span className="text-muted-foreground flex items-center gap-1.5"><Mail className="w-3.5 h-3.5"/> To:</span>
                  <span className="font-medium break-all">{log.to}</span>
                </div>
                {log.cc && (
                  <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
                    <span className="text-muted-foreground text-right mr-2">Cc:</span>
                    <span className="break-all">{log.cc}</span>
                  </div>
                )}
                {log.bcc && (
                  <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
                    <span className="text-muted-foreground text-right mr-2">Bcc:</span>
                    <span className="break-all">{log.bcc}</span>
                  </div>
                )}
                <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
                  <span className="text-muted-foreground flex items-center gap-1.5"><Clock className="w-3.5 h-3.5"/> Time:</span>
                  <span>{new Date(log.sentAt).toLocaleString()}</span>
                </div>
                {log.templateName && (
                  <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
                    <span className="text-muted-foreground flex items-center gap-1.5"><Code className="w-3.5 h-3.5"/> Template:</span>
                    <Link href={`/app/templates/${log.templateId}`} className="text-primary hover:underline">{log.templateName}</Link>
                  </div>
                )}
                {log.providerMessageId && (
                  <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
                    <span className="text-muted-foreground text-right mr-2">Message ID:</span>
                    <span className="font-mono text-xs break-all bg-muted p-1 rounded">{log.providerMessageId}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {log.variables && Object.keys(log.variables).length > 0 && (
              <Card
                className="shadow-none border-border"
                data-testid="log-detail-variables-card"
              >
                <CardContent className="p-4 space-y-3 text-sm">
                  <div>
                    <h3 className="text-sm font-semibold">Variables used</h3>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Snapshot of the template variable values rendered into
                      this email at send time.
                    </p>
                  </div>
                  <dl className="space-y-2">
                    {Object.entries(log.variables).map(([key, value]) => (
                      <div
                        key={key}
                        className="grid grid-cols-[120px_1fr] gap-2 items-start"
                        data-testid={`log-detail-variable-${key}`}
                      >
                        <dt className="text-muted-foreground font-mono text-xs break-all">
                          {key}
                        </dt>
                        <dd className="font-medium text-xs break-words whitespace-pre-wrap">
                          {value === "" ? (
                            <span className="text-muted-foreground italic">
                              (empty)
                            </span>
                          ) : (
                            value
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </CardContent>
              </Card>
            )}

            {log.status === "sent" && log.engagement && (
              <Card
                className="shadow-none border-border"
                data-testid="log-detail-engagement-card"
              >
                <CardContent className="p-4 space-y-3 text-sm">
                  <div>
                    <h3 className="text-sm font-semibold">Engagement</h3>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Opens and clicks recorded for this email. Likely-bot
                      events (Apple Mail Privacy Protection, link scanners)
                      are excluded.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div
                      className="rounded-md border border-border bg-muted/30 p-3"
                      data-testid="log-detail-engagement-opens"
                    >
                      <div className="flex items-center gap-1.5 text-muted-foreground text-[11px] uppercase tracking-widest">
                        <Eye className="w-3.5 h-3.5" /> Opens
                      </div>
                      <div className="mt-1 text-2xl font-semibold">
                        {log.engagement.opens}
                      </div>
                      {log.engagement.firstOpenAt && (
                        <div className="mt-1 text-[11px] text-muted-foreground">
                          First: {new Date(log.engagement.firstOpenAt).toLocaleString()}
                        </div>
                      )}
                    </div>
                    <div
                      className="rounded-md border border-border bg-muted/30 p-3"
                      data-testid="log-detail-engagement-clicks"
                    >
                      <div className="flex items-center gap-1.5 text-muted-foreground text-[11px] uppercase tracking-widest">
                        <MousePointerClick className="w-3.5 h-3.5" /> Clicks
                      </div>
                      <div className="mt-1 text-2xl font-semibold">
                        {log.engagement.clicks}
                      </div>
                      {log.engagement.firstClickAt && (
                        <div className="mt-1 text-[11px] text-muted-foreground">
                          First: {new Date(log.engagement.firstClickAt).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                  {log.engagement.clickedUrls.length > 0 && (() => {
                    // Show every recorded URL ranked by clicks. The
                    // bar width is scaled to the most-clicked URL so
                    // the top link is always full width and the rest
                    // are proportional to it. Unique-clicker counts
                    // are surfaced alongside total clicks so a single
                    // recipient hammering one link doesn't get
                    // mistaken for broad engagement.
                    const ranked = log.engagement.clickedUrls;
                    const maxClicks = ranked.reduce(
                      (m, u) => (u.clicks > m ? u.clicks : m),
                      0,
                    );
                    return (
                      <div
                        className="space-y-1.5"
                        data-testid="log-detail-engagement-clicked-urls"
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Top clicked links
                          </div>
                          <div className="text-[10px] text-muted-foreground">
                            clicks · unique
                          </div>
                        </div>
                        <ul className="space-y-2">
                          {ranked.map((u, i) => {
                            const pct =
                              maxClicks > 0
                                ? Math.max(4, Math.round((u.clicks / maxClicks) * 100))
                                : 0;
                            return (
                              <li
                                key={`${u.url}-${i}`}
                                className="space-y-1"
                                data-testid={`log-detail-engagement-url-${i}`}
                                data-clicks={u.clicks}
                                data-unique-clickers={u.uniqueClickers}
                              >
                                <div className="flex items-center gap-2 text-xs">
                                  <span
                                    className="font-mono text-primary truncate flex-1"
                                    title={u.url}
                                  >
                                    {u.url}
                                  </span>
                                  <span
                                    className="text-foreground font-medium tabular-nums shrink-0"
                                    title={`${u.clicks} total click${u.clicks === 1 ? "" : "s"}`}
                                  >
                                    {u.clicks}
                                  </span>
                                  <span
                                    className="text-muted-foreground tabular-nums shrink-0 w-8 text-right"
                                    title={`${u.uniqueClickers} unique clicker${u.uniqueClickers === 1 ? "" : "s"} (approximated by IP + user-agent)`}
                                    data-testid={`log-detail-engagement-url-${i}-unique`}
                                  >
                                    · {u.uniqueClickers}
                                  </span>
                                </div>
                                <div
                                  className="h-1.5 rounded-full bg-muted overflow-hidden"
                                  aria-hidden="true"
                                >
                                  <div
                                    className="h-full bg-primary/70 rounded-full transition-all"
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })()}
                  {log.engagement.opens === 0 &&
                    log.engagement.clicks === 0 &&
                    log.engagement.clickedUrls.length === 0 && (
                      <p
                        className="text-[11px] text-muted-foreground italic"
                        data-testid="log-detail-engagement-empty"
                      >
                        No engagement recorded yet. If tracking was disabled
                        in Brand Settings when this was sent, no events will
                        ever appear here.
                      </p>
                    )}
                </CardContent>
              </Card>
            )}

            {log.thread && log.thread.messages.length > 0 && (
              <Card
                className="shadow-none border-border"
                data-testid="log-detail-thread-card"
                data-thread-count={log.thread.messages.length}
              >
                <CardContent className="p-4 space-y-3 text-sm">
                  <div>
                    <h3 className="text-sm font-semibold">Conversation</h3>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Every inbound and outbound message on this thread,
                      oldest first. Click any message to open it.
                    </p>
                  </div>
                  <ol className="space-y-1.5">
                    {log.thread.messages.map((m, idx) => {
                      const isOutbound = m.kind === "outbound";
                      const isCurrent = isOutbound && m.id === log.id;
                      const href = isOutbound
                        ? `/app/logs/${m.id}`
                        : `/app/inbox/${m.id}`;
                      const rawSnippet =
                        (m.textBody && m.textBody.trim().length > 0
                          ? m.textBody
                          : m.htmlBody
                            ? m.htmlBody.replace(/<[^>]*>/g, " ")
                            : "")
                          .replace(/\s+/g, " ")
                          .trim();
                      const snippet =
                        rawSnippet.length > 90
                          ? `${rawSnippet.slice(0, 90)}…`
                          : rawSnippet;
                      const sender = m.fromName ?? m.fromAddress;
                      // The currently-open log row is rendered as a
                      // non-link "you are here" marker so users don't
                      // get a confusing self-navigation.
                      const inner = (
                        <div
                          className={`rounded-md border px-3 py-2 text-xs leading-snug transition-colors ${
                            isCurrent
                              ? "bg-primary/10 border-primary/30"
                              : isOutbound
                                ? "bg-primary/5 border-primary/20 hover:bg-primary/10"
                                : "bg-muted/30 border-border hover:bg-muted/50"
                          }`}
                        >
                          <div className="flex items-center gap-1.5 font-medium">
                            {isOutbound ? (
                              <Send className="w-3 h-3 text-primary shrink-0" />
                            ) : (
                              <ArrowDownRight className="w-3 h-3 text-muted-foreground shrink-0" />
                            )}
                            <span className="truncate">{sender}</span>
                            {!isOutbound && (
                              <>
                                <VerifiedSenderBadge
                                  verificationStatus={m.verificationStatus ?? null}
                                  testIdSuffix={`-log-thread-${idx}`}
                                />
                                <FailedVerificationWarning
                                  verificationStatus={m.verificationStatus ?? null}
                                  verificationDetails={m.verificationDetails ?? null}
                                  testIdSuffix={`-log-thread-${idx}`}
                                />
                              </>
                            )}
                            <span className="ml-auto text-[10px] text-muted-foreground shrink-0">
                              {new Date(m.sentAt).toLocaleString()}
                            </span>
                          </div>
                          {snippet && (
                            <div className="mt-1 text-muted-foreground line-clamp-2">
                              {snippet}
                            </div>
                          )}
                          {m.attachments.length > 0 && (
                            <div className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground">
                              <Paperclip className="w-3 h-3" />
                              {m.attachments.length} attachment
                              {m.attachments.length === 1 ? "" : "s"}
                            </div>
                          )}
                          {!isCurrent && (
                            <div className="mt-1 text-[10px] text-primary inline-flex items-center gap-1">
                              Open
                              <ExternalLink className="w-3 h-3" />
                            </div>
                          )}
                          {isCurrent && (
                            <div className="mt-1 text-[10px] uppercase tracking-widest text-primary font-medium">
                              You are here
                            </div>
                          )}
                        </div>
                      );
                      return (
                        <li
                          key={`${m.kind}-${m.id}`}
                          data-testid={`log-detail-thread-message-${idx}`}
                          data-thread-message-kind={m.kind}
                          data-thread-message-current={
                            isCurrent ? "true" : "false"
                          }
                        >
                          {isCurrent ? inner : <Link href={href}>{inner}</Link>}
                        </li>
                      );
                    })}
                  </ol>
                </CardContent>
              </Card>
            )}

            {log.status === "failed" && (log.errorMessage || log.providerErrorMessage) && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-destructive flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4" /> Error Details
                </h3>
                {log.errorMessage && (
                  <p className="text-xs text-destructive/90 break-words leading-relaxed">
                    {log.errorMessage}
                  </p>
                )}
                {log.providerErrorMessage && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setShowProviderDetails((v) => !v)}
                      className="flex items-center gap-1 text-xs font-medium text-destructive/80 hover:text-destructive"
                      data-testid="button-toggle-provider-details"
                    >
                      {showProviderDetails ? (
                        <ChevronDown className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5" />
                      )}
                      {showProviderDetails
                        ? "Hide provider details"
                        : "Show provider details"}
                    </button>
                    {showProviderDetails && (
                      <pre
                        className="mt-2 text-[11px] text-destructive/90 break-words whitespace-pre-wrap font-mono bg-destructive/5 border border-destructive/10 rounded p-2"
                        data-testid="text-provider-error-message"
                      >
                        {log.providerErrorMessage}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Preview Side */}
        <div className="w-full lg:flex-1 bg-[#f4f4f5] flex flex-col relative overflow-hidden">
          <div className="p-3 border-b border-border/50 bg-white/50 flex justify-between items-center shrink-0">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Rendered Output</span>
          </div>
          <div className="flex-1 p-4 sm:p-8 overflow-auto flex items-start justify-center">
            <div className="bg-white shadow-xl rounded-md w-full max-w-[800px] overflow-hidden flex flex-col border border-border/10 h-full min-h-[500px]">
              <iframe 
                srcDoc={log.html ? rewriteLinksForPreview(log.html) : `<pre>${escapeHtml(log.text || 'No content')}</pre>`}
                sandbox="allow-popups allow-popups-to-escape-sandbox"
                className="w-full flex-1 bg-white border-0" 
                title="Email Content"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
